import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Settings, Zap, Package, CheckCircle, Phone } from 'lucide-react';

const services = [
  {
    icon: <Wrench size={40} />,
    title: 'American V-Twin Repair',
    desc: "Whether your bike is throwing codes, making noise, or just not running right, we diagnose and fix it properly. We work on Harley-Davidson, Indian, and other American V-Twin platforms with real expertise.",
    items: ['Engine diagnostics & repair', 'Transmission service', 'Primary & clutch work', 'Electrical troubleshooting', 'Brake & suspension service', 'Routine maintenance & tune-ups'],
  },
  {
    icon: <Settings size={40} />,
    title: 'Custom Builds',
    desc: "We build bikes from the frame up or help you complete a project that's been stalled. Every custom build gets the same attention to detail as a daily rider, because it's going on the road.",
    items: ['Frame-up custom builds', 'Bobbers & choppers', 'Resto-mods & cafe builds', 'Fabrication & welding', 'Wiring & electrical buildout', 'Final assembly & setup'],
  },
  {
    icon: <Zap size={40} />,
    title: 'Performance Upgrades',
    desc: 'More power, better throttle response, smoother delivery. We do real performance work backed by experience, not just bolt-on parts.',
    items: ['Cam & head work', 'Big bore kits', 'Intake & exhaust upgrades', 'Fuel & ignition tuning', 'Dyno testing & tuning', 'Stage 1, 2 & 3 upgrades'],
  },
  {
    icon: <Package size={40} />,
    title: 'Rare Parts Sourcing',
    desc: "Can't find the part you need? We can. We have the connections and the patience to track down rare, discontinued, and hard-to-find parts for builds and restorations.",
    items: ['Discontinued OEM parts', 'NOS (new old stock) hunting', 'Aftermarket sourcing', 'Vintage & classic bike parts', 'Custom fabricated replacements', 'Parts verification & fitment'],
  },
];

const css = `
  .svc-page { background: #0d0d0d; }
  .svc-list { max-width: 1180px; margin: 0 auto; padding: 80px 24px; display: flex; flex-direction: column; gap: 60px; }
  .svc-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: start;
    padding-bottom: 60px; border-bottom: 1px solid rgba(255,255,255,0.07);
  }
  .svc-row:last-child { border-bottom: none; padding-bottom: 0; }
  .svc-row:nth-child(even) .svc-panel { order: 2; }
  .svc-row:nth-child(even) .svc-checklist { order: 1; }
  .svc-panel {
    background: #1a1a1a; border: 1px solid rgba(255,255,255,0.06);
    border-top: 3px solid #c0392b; padding: 44px 36px;
  }
  .svc-panel-icon { color: #c0392b; margin-bottom: 18px; }
  .svc-panel h3 { font-size: 1.5rem; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 14px; }
  .svc-panel p { font-size: 0.93rem; color: #888; line-height: 1.75; }
  .svc-checklist h4 {
    font-family: 'Oswald', sans-serif; font-size: 0.74rem; font-weight: 600;
    letter-spacing: 0.22em; text-transform: uppercase; color: #d4a017; margin-bottom: 20px;
  }
  .svc-checklist ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 14px; }
  .svc-checklist ul li { display: flex; align-items: flex-start; gap: 12px; font-size: 0.93rem; color: #bbb; }
  .svc-checklist ul li svg { color: #c0392b; flex-shrink: 0; margin-top: 2px; }
  .svc-bottom { background: #111; border-top: 1px solid rgba(255,255,255,0.07); padding: 80px 0; text-align: center; }
  .svc-bottom h2 { font-size: clamp(1.8rem,4vw,3rem); text-transform: uppercase; color: #fff; margin-bottom: 12px; }
  .svc-bottom p { color: #888; margin-bottom: 36px; }
  .svc-bottom-actions { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }
  @media (max-width:768px) {
    .svc-row { grid-template-columns: 1fr; }
    .svc-row:nth-child(even) .svc-panel, .svc-row:nth-child(even) .svc-checklist { order: unset; }
  }
`;

export default function Services() {
  return (
    <>
      <style>{css}</style>
      <div className="page-header">
        <div className="container">
          <span className="section-label">What We Do</span>
          <h1>Our Services</h1>
          <p>From routine maintenance to full custom builds. We work on American V-Twins and we do it right.</p>
        </div>
      </div>
      <div className="svc-page">
        <div className="svc-list">
          {services.map((s, i) => (
            <div className="svc-row" key={i}>
              <div className="svc-panel">
                <div className="svc-panel-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
              <div className="svc-checklist">
                <h4>Includes</h4>
                <ul>
                  {s.items.map((item, j) => (
                    <li key={j}><CheckCircle size={16} /> {item}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <div className="svc-bottom">
          <h2>Have a Job in Mind?</h2>
          <p>Give us a call and we'll talk through what you need and give you straight answers.</p>
          <div className="svc-bottom-actions">
            <a href="tel:7172785897" className="btn-primary"><Phone size={16} /> (717) 278-5897</a>
            <Link to="/contact" className="btn-outline">Get in Touch</Link>
          </div>
        </div>
      </div>
    </>
  );
}
